import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, X, Percent, Save, Search, Star, UploadCloud, FileCheck2 } from "lucide-react";

const fmtEur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n) || 0);
const fmtNum = (n) => new Intl.NumberFormat("es-ES").format(Math.round(Number(n) || 0));

/**
 * Iter-62 — Descuentos negociados con laboratorios DE MARCA.
 * Muestra todos los labs de marca del estudio con su volumen (uds + €) y
 * permite asignarles un % de descuento adicional. FarmaClass calcula el
 * "beneficio real de marca" = € marca × % dto / 100 y lo suma en el total.
 * Se guarda en el mismo `supplier_agreements` bajo `brand_discount_pct`, así
 * queda al lado del resto de condiciones y no hay que duplicar datos.
 */
export default function BrandDiscountsModal({ dispId, onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ total_marca_euros: 0, total_benefit_eur: 0, labs_with_discount: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [onlyWithDiscount, setOnlyWithDiscount] = useState(false);
  const [dirty, setDirty] = useState({}); // { lab: newPct }
  const [uploadingLab, setUploadingLab] = useState(null);
  const [scaleRows, setScaleRows] = useState({}); // { labUpper: rowsCount }

  // Carga inicial de qué labs ya tienen escalado subido (para pintar chip)
  useEffect(() => {
    if (!rows.length) return;
    let alive = true;
    Promise.all(rows.map((r) =>
      api.get(`/brand-discounts/${encodeURIComponent(r.laboratorio)}/scale`)
        .then((res) => ({ lab: r.laboratorio.toUpperCase(), n: (res.data?.rows || []).length }))
        .catch(() => ({ lab: r.laboratorio.toUpperCase(), n: 0 }))
    )).then((results) => {
      if (!alive) return;
      const map = {};
      for (const { lab, n } of results) if (n > 0) map[lab] = n;
      setScaleRows(map);
    });
    return () => { alive = false; };
  }, [rows]);

  const handleUploadFile = async (lab, file) => {
    if (!file) return;
    setUploadingLab(lab);
    try {
      const raw = await file.arrayBuffer();
      const { data } = await api.post(
        `/brand-discounts/${encodeURIComponent(lab)}/upload-scale`,
        raw,
        { headers: { "Content-Type": "application/octet-stream" } },
      );
      toast.success(`${lab}: ${data.rows} CN cargados del escalado`);
      setScaleRows((prev) => ({ ...prev, [lab.toUpperCase()]: data.rows }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo procesar el fichero");
    } finally {
      setUploadingLab(null);
    }
  };

  useEffect(() => {
    if (!dispId) return;
    let alive = true;
    setLoading(true);
    api.get(`/dispensations/${dispId}/brand-labs`)
      .then(({ data }) => {
        if (!alive) return;
        setRows(data?.labs || []);
        setTotals(data?.totals || { total_marca_euros: 0, total_benefit_eur: 0, labs_with_discount: 0 });
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "No se pudo cargar la lista de labs de marca"))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [dispId]);

  // Cambio de % (solo estado local, batch al pulsar guardar)
  const changePct = (lab, value) => {
    setDirty((prev) => ({ ...prev, [lab]: value }));
  };

  const effectivePct = (lab, storedPct) => {
    const raw = dirty[lab];
    if (raw == null) return storedPct;
    const parsed = parseFloat(String(raw).replace(",", "."));
    return isNaN(parsed) ? storedPct : Math.max(0, Math.min(100, parsed));
  };

  const previewBenefit = useMemo(() => {
    let sum = 0;
    for (const r of rows) sum += (Number(r.euros) || 0) * effectivePct(r.laboratorio, Number(r.brand_discount_pct) || 0) / 100;
    return sum;
  }, [rows, dirty]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (q && !r.laboratorio.toLowerCase().includes(q)) return false;
      if (onlyWithDiscount) {
        const p = effectivePct(r.laboratorio, Number(r.brand_discount_pct) || 0);
        if (p <= 0) return false;
      }
      return true;
    });
  }, [rows, query, onlyWithDiscount, dirty]);

  const saveAll = async () => {
    const items = [];
    for (const [lab, val] of Object.entries(dirty)) {
      const parsed = parseFloat(String(val).replace(",", "."));
      if (isNaN(parsed)) continue;
      items.push({ laboratorio: lab, brand_discount_pct: Math.max(0, Math.min(100, parsed)) });
    }
    if (!items.length) {
      toast.info("No hay cambios que guardar");
      return;
    }
    setSaving(true);
    try {
      await api.post("/brand-discounts/batch", { items });
      toast.success(`Descuentos guardados: ${items.length} laboratorio(s)`);
      setDirty({});
      onSaved && onSaved();
      // Notifica al resto de la app (DispensationDetailPage escucha para
      // refrescar las estrellas ⭐ en la tabla de marca y expansiones).
      try { window.dispatchEvent(new Event("brand-discounts-saved")); } catch (_) { /* noop */ }
      // Recargar totales frescos
      const { data } = await api.get(`/dispensations/${dispId}/brand-labs`);
      setRows(data?.labs || []);
      setTotals(data?.totals || totals);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const dirtyCount = Object.keys(dirty).length;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="brand-discounts-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-slate-100">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-violet-600">Condiciones · Marca</div>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-950">
              Descuentos negociados con labs de marca
            </h2>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Algunos labs de marca dan una condición extra que no es la estándar. Marca los que apliquen y
              el % que has negociado — FarmaClass sumará el <strong>beneficio real de marca</strong> a tus KPIs.
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-slate-100" data-testid="brand-discounts-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Toolbar + totals */}
        <div className="p-4 border-b border-slate-100 bg-slate-50/60 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white rounded-lg border border-slate-200 p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Compra marca total</div>
              <div className="font-mono-num text-lg font-bold text-slate-900">{fmtEur(totals.total_marca_euros)}</div>
            </div>
            <div className="bg-white rounded-lg border border-violet-200 p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-violet-600">Beneficio real de marca</div>
              <div className="font-mono-num text-lg font-bold text-violet-700" data-testid="brand-benefit-preview">
                {fmtEur(previewBenefit)}
                {dirtyCount > 0 && <span className="text-[10px] text-amber-600 ml-1">(preview)</span>}
              </div>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Labs con dto negociado</div>
              <div className="font-mono-num text-lg font-bold text-slate-900">{totals.labs_with_discount} / {rows.length}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar laboratorio..."
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-200"
                data-testid="brand-discounts-search"
              />
            </div>
            <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer whitespace-nowrap">
              <input type="checkbox" checked={onlyWithDiscount} onChange={(e) => setOnlyWithDiscount(e.target.checked)} className="h-3.5 w-3.5 rounded" data-testid="brand-discounts-only-with" />
              Solo con % configurado
            </label>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-center py-16 text-slate-500 text-sm"><Loader2 className="h-5 w-5 animate-spin inline mr-1.5" /> Cargando labs de marca…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-slate-500 text-sm">Sin resultados</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Laboratorio</th>
                  <th className="text-right p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">CN</th>
                  <th className="text-right p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Uds</th>
                  <th className="text-right p-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">€ Marca</th>
                  <th className="text-center p-3 text-[10px] uppercase tracking-wider font-bold text-violet-600">% Dto negociado</th>
                  <th className="text-center p-3 text-[10px] uppercase tracking-wider font-bold text-violet-600">Escalado (Excel)</th>
                  <th className="text-right p-3 text-[10px] uppercase tracking-wider font-bold text-violet-600">Beneficio €</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const stored = Number(r.brand_discount_pct) || 0;
                  const eff = effectivePct(r.laboratorio, stored);
                  const benefit = r.euros * eff / 100;
                  const isDirty = dirty[r.laboratorio] != null && parseFloat(String(dirty[r.laboratorio]).replace(",", ".")) !== stored;
                  return (
                    <tr key={r.laboratorio} className={`border-t border-slate-100 hover:bg-violet-50/30 ${isDirty ? "bg-amber-50/60" : ""}`} data-testid={`brand-row-${r.laboratorio}`}>
                      <td className="p-3 font-medium text-slate-900">
                        <div className="inline-flex items-center gap-1.5">
                          {eff > 0 && <Star className="h-3 w-3 text-violet-500 fill-violet-500" />}
                          {r.laboratorio}
                        </div>
                      </td>
                      <td className="p-3 text-right font-mono-num text-slate-500 text-xs">{r.cn_count}</td>
                      <td className="p-3 text-right font-mono-num text-slate-700">{fmtNum(r.units)}</td>
                      <td className="p-3 text-right font-mono-num font-semibold text-slate-900">{fmtEur(r.euros)}</td>
                      <td className="p-3">
                        <div className="inline-flex items-center gap-1 justify-center w-full">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.5"
                            value={dirty[r.laboratorio] ?? stored}
                            onChange={(e) => changePct(r.laboratorio, e.target.value)}
                            className="w-20 px-2 py-1 border border-slate-300 rounded text-right font-mono-num text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                            data-testid={`brand-pct-${r.laboratorio}`}
                          />
                          <Percent className="h-3.5 w-3.5 text-slate-400" />
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        <label className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-md cursor-pointer transition-colors ${scaleRows[r.laboratorio.toUpperCase()] ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200 border border-emerald-300" : "bg-violet-100 text-violet-800 hover:bg-violet-200 border border-violet-300"}`}
                               title="Sube el Excel del distribuidor con CN + PVL + Desc s/PVL (franja 1-5 uds)"
                               data-testid={`brand-upload-${r.laboratorio}`}>
                          {uploadingLab === r.laboratorio ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : scaleRows[r.laboratorio.toUpperCase()] ? (
                            <><FileCheck2 className="h-3.5 w-3.5" /> {scaleRows[r.laboratorio.toUpperCase()]} CN</>
                          ) : (
                            <><UploadCloud className="h-3.5 w-3.5" /> Subir</>
                          )}
                          <input
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            className="hidden"
                            disabled={uploadingLab === r.laboratorio}
                            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) handleUploadFile(r.laboratorio, f); }}
                          />
                        </label>
                      </td>
                      <td className={`p-3 text-right font-mono-num font-bold ${benefit > 0 ? "text-violet-700" : "text-slate-400"}`}>
                        {fmtEur(benefit)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-100 bg-white flex items-center justify-between">
          <div className="text-xs text-slate-500">
            {dirtyCount > 0 ? (
              <span className="text-amber-700 font-semibold">
                {dirtyCount} cambio(s) sin guardar
              </span>
            ) : "Cambia los % y pulsa Guardar."}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="brand-discounts-cancel">
              Cerrar
            </button>
            <button
              onClick={saveAll}
              disabled={saving || dirtyCount === 0}
              className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold px-5 py-2 rounded-md text-sm"
              data-testid="brand-discounts-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Guardar cambios
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
